// What a session's row says, in the order a teacher (or the learner) reads it:
// what it was, how long, then the details. Pure, so it can be tested.
import { formatTime } from "./sessionStats";

// Browsing has been recorded since this day; before it, its absence means nothing.
export const BROWSING_TRACKED_SINCE = "2025-12-11";

export function sessionTitle(session) {
  switch (session.session_type) {
    case "reading":
      return session.article_title || "Reading";
    case "audio":
      return session.lesson_title || "Audio lesson";
    case "exercise":
      return "Exercises";
    default:
      return "Browsed the article list";
  }
}

// "45 s", "6 min", "1 h 5 min": a duration at a glance.
export function durationLabel(ms) {
  const seconds = Math.round((ms || 0) / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${Math.floor(minutes / 60)} h`;
}

const lookedUp = (n) => (n > 0 ? `${n} ${n === 1 ? "word" : "words"} looked up` : null);

// The muted line under the title.
export function sessionDetails(session) {
  const details = [formatTime(session.start_time)];
  switch (session.session_type) {
    case "reading":
      if (session.article_word_count > 0) details.push(`${session.article_word_count} words`);
      if (session.article_cefr_level) details.push(session.article_cefr_level);
      if (session.reading_completion > 0) details.push(`read ${Math.round(session.reading_completion * 100)}%`);
      details.push(lookedUp(session.word_count));
      break;
    case "exercise":
      details.push(`${session.word_count} ${session.word_count === 1 ? "word" : "words"}`);
      details.push(`${session.accuracy}% correct`);
      break;
    case "audio":
      details.push(session.completed ? `${session.word_count} words` : "not finished");
      break;
    default:
      details.push(lookedUp(session.word_count));
  }
  return details.filter(Boolean);
}

// How many of the words looked up in a session were practised in exercises
// afterwards. An exercise session's words are the practice itself: 0.
export function practisedCount(session) {
  if (session.session_type === "exercise") return 0;
  return (session.words || []).filter((word) => word.practised).length;
}

// A session with nothing to show but its time: one line, not a card.
// Browsing without translations, and an article opened for under a minute
// without a word looked up (still worth knowing: a text given up on).
export function isSlim(session) {
  if (session.word_count > 0) return false;
  if (session.session_type === "browsing") return true;
  return session.session_type === "reading" && session.duration < 60 * 1000;
}

// Too short to mean anything: under 30 s with no word looked up (an
// accidental tap, an article opened and backed out of). Left out of the list;
// the summary above still counts its seconds.
export function isBlip(session) {
  return !(session.word_count > 0) && session.duration < 30 * 1000;
}

export function showsBrowsingNote(sessions) {
  return sessions.some((s) => new Date(s.start_time) < new Date(BROWSING_TRACKED_SINCE));
}

// An exercise session's words, each once, with its results in order, split
// into the ones that needed help (any wrong answer) and the ones right every
// time. A word practised twice used to show twice: "chaque ✓ … chaque ✗".
export function groupExerciseWords(words) {
  const byWord = new Map();
  (words || []).forEach((word) => {
    const key = `${word.origin}\u0000${word.translation}`;
    if (!byWord.has(key)) byWord.set(key, { ...word, results: [] });
    byWord.get(key).results.push(!!word.correct);
  });
  const all = [...byWord.values()];
  return {
    neededHelp: all.filter((w) => w.results.includes(false)),
    right: all.filter((w) => !w.results.includes(false)),
  };
}

// What used to say "Distracted": the fact behind it. Shown when the API judges
// the session distracted (several departures, often): "left the page 9 times".
export function leftThePage(session) {
  if (session.focus_level !== "distracted" || !(session.interruptions > 0)) return null;
  const n = session.interruptions;
  return `left the page ${n} ${n === 1 ? "time" : "times"}`;
}

// A browsing session's words under the article whose title or summary they
// were translated in, articles in the order first translated in. Words with
// no article (older sessions, search) form a group with no title.
export function groupBrowsingWords(words) {
  const byArticle = new Map();
  (words || []).forEach((word) => {
    const key = word.article_id ?? null;
    if (!byArticle.has(key)) byArticle.set(key, { articleId: key, title: word.article_title ?? null, words: [] });
    byArticle.get(key).words.push(word);
  });
  return [...byArticle.values()];
}

// A reading session's time off the clock, as stretches of a strip that spans
// the session from start to last action: { left, width } in percent and its
// kind -- "away" (the page open behind another tab or app), "idle" (in focus,
// no scroll, tap or key), "closed" (between two readings in one entry).
// Each stretch is drawn at least a few pixels wide (see FocusStrip), so a
// two-second departure still shows as a tick.
export function awayStretches(session) {
  const start = new Date(session.start_time).getTime();
  const span = new Date(session.end_time).getTime() - start;
  if (!(span > 0)) return [];
  const percent = (time) => Math.min(100, Math.max(0, ((new Date(time).getTime() - start) / span) * 100));
  return (session.away || []).map(([from, to, why]) => ({
    left: percent(from),
    width: percent(to) - percent(from),
    kind: why || "away",
  }));
}

const lengthOf = ([from, to]) => Math.max(0, new Date(to) - new Date(from));
const awayOfKind = (session, kind) =>
  (session.away || []).filter(([, , why]) => (why || "away") === kind).reduce((sum, i) => sum + lengthOf(i), 0);

// How long a session was open, from start to last action, in ms.
export function openSpan(session) {
  return Math.max(0, new Date(session.end_time) - new Date(session.start_time));
}

// A length in percent of the card, on the same scale as the summary bar
// above the sessions (scale: its total, in ms). Longer than that, e.g. an
// article left open overnight, stops at the full width.
export function scaledPercent(ms, scale) {
  if (!(scale > 0)) return 100;
  return Math.min(100, Math.max(0, (ms / scale) * 100));
}

// How long a reading's page was open but out of focus (in another tab or
// app), in ms; idle in focus and the article closed do not count.
export function outOfFocusMs(session) {
  return awayOfKind(session, "away");
}

// How long a reading's page was open in focus but idle, in ms. Readers before
// Oct 2026 did not log idle: for their sessions it is whatever time the
// article was open that the clock, the time away and the closed stretches
// do not account for -- idle somewhere on the strip, but nobody knows where.
export function idleMs(session) {
  if (session.idle_tracked) return awayOfKind(session, "idle");
  const accounted = (session.duration || 0) + (session.away || []).reduce((sum, i) => sum + lengthOf(i), 0);
  return Math.max(0, openSpan(session) - accounted);
}

// A session whose idle time is unknown and too long to draw as if it were
// reading (an article left open for hours): its strip shows only what is
// known, in proportion, instead of the open time.
const PLACELESS_IDLE_MS = 5 * 60 * 1000;
export function idleUnplaced(session) {
  return !session.idle_tracked && idleMs(session) > PLACELESS_IDLE_MS;
}

// For such a session: active, then away, then closed, as parts of a strip as
// long as their sum (ms). { length, stretches }.
export function summaryStrip(session) {
  const active = session.duration || 0;
  const away = outOfFocusMs(session);
  const closed = awayOfKind(session, "closed");
  const length = active + away + closed;
  if (!(length > 0)) return { length: 0, stretches: [] };
  const pct = (ms) => (ms / length) * 100;
  const stretches = [];
  if (away > 0) stretches.push({ left: pct(active), width: pct(away), kind: "away" });
  if (closed > 0) stretches.push({ left: pct(active + away), width: pct(closed), kind: "closed" });
  return { length, stretches };
}
